import { useCallback, useState } from 'react'

import useCollegeName from 'common/use-college-name'
import { useGuaranteedScid } from 'common/use-scid'
import useSnackbar from 'common/use-snackbar'

import ContentManagementContext, {
  BulkContentItem,
  Content,
  ContentItem,
  ContentItemObject,
  ContentType,
  FilterBy,
  SortBy,
  SortDirection,
  TopicFilter,
} from './Context'
import {
  OverviewResponse,
  SaveOrderArgs,
  createContentItem,
  deleteContent,
  getContent,
  getContentItem,
  getHighlights,
  updateContentItem,
  updateContentPostType,
  updateContentVisibility,
  updateHighlights,
  updateOrderFromSortIndex,
} from './content-service'

const ContentManagementProvider = ({ children }: { children: React.ReactNode }) => {
  const scid = useGuaranteedScid()
  const collegeName = useCollegeName()
  const DEFAULT_PAGE_SIZE = 10
  // TEMPORARY
  const [overview, setOverview] = useState<OverviewResponse>()

  /** Highlights State */
  const [highlights, setHighlights] = useState<string[]>([])
  const [fetchHighlightsLoading, setFetchHighlightsLoading] = useState(true)
  const [saveHighlightsError, setSaveHighlightsError] = useState(false)
  const [saveHighlightsSucceeded, setSaveHighlightsSucceeded] = useState(false)
  const fetchHighlightsError = useSnackbar('An error occurred while fetching your Highlights, please try again.')

  /** Content State */
  const [content, setContent] = useState<Content>()
  const [fetchContentLoading, setFetchContentLoading] = useState(true)
  const [saveContentLoading, setSaveContentLoading] = useState(false)
  const [saveContentError, setSaveContentError] = useState<boolean>(false)
  const [saveContentSucceeded, setSaveContentSucceeded] = useState(false)
  const [focusedContentItem, setFocusedContentItem] = useState<ContentItemObject | undefined>()
  const [selectedContent, setSelectedContent] = useState<BulkContentItem[] | undefined>()
  const [modalMode, setModalMode] = useState<'edit' | 'copy' | 'new'>('edit')

  const fetchContentError = useSnackbar('An error occurred while fetching your Content, please try again.')
  const bulkDeleteContentError = useSnackbar('An error occurred while deleting your Content, please try again.')
  const updateContentVisibilityError = useSnackbar('An error occurred while updating your Content, please try again.')

  /** Reorder Content state */
  const [reorderContent, setReorderContent] = useState<Content>()
  const [fetchReorderContentLoading, setFetchReorderContentLoading] = useState(true)
  const [saveNewOrderError, setSaveNewOrderError] = useState(false)

  /** Content Pagination */
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [from, setFrom] = useState(0)

  /** Table Filtering */
  const [filterByStatus, setFilterByStatus] = useState<boolean>()
  const [filterByType, setFilterByType] = useState<string[]>()
  const [filterByTopics, setFilterByTopics] = useState<TopicFilter>()
  const [untaggedOnly, setUntaggedOnly] = useState<boolean>(false)

  const handleTableFiltering = useCallback(async (filter?: FilterBy, value?: boolean | string[] | TopicFilter) => {
    // Reset Pagination
    setPageSize(DEFAULT_PAGE_SIZE)
    setFrom(0)
    // Reset Selection
    setSelectedContent(undefined)
    // Filter
    switch (filter) {
      case 'Status':
        setFilterByStatus(value as boolean)
        break
      case 'Type':
        setFilterByType(value as string[])
        break
      case 'Topics':
        setFilterByTopics(value as TopicFilter)
        break
      case 'Untagged':
        setUntaggedOnly(value as boolean)
        setFilterByTopics(undefined)
        break
      default:
        setFilterByStatus(undefined)
        setFilterByType(undefined)
        setFilterByTopics(undefined)
        setUntaggedOnly(false)
        break
    }
  }, [])

  /** Table Sorting */
  const [orderBy, setOrderBy] = useState<SortBy>()
  const [order, setOrder] = useState<SortDirection>('desc')

  const handleTableSorting = useCallback(async (key: SortBy, dir: SortDirection) => {
    // Reset Pagination
    setPageSize(DEFAULT_PAGE_SIZE)
    setFrom(0)
    // Sort
    setOrderBy(key)
    setOrder(dir)
  }, [])

  /** Reorder Actions */

  const saveNewOrder = ({ type, newOrder, onError }: SaveOrderArgs) => {
    updateOrderFromSortIndex({ scid, type, newOrder })
      .then(() => {
        setSaveNewOrderError(false)
        fetchContent()
      })
      .catch(() => {
        setSaveNewOrderError(true)
        onError()
      })
  }

  /** Bulk Actions */
  const onSelectAllContent = (checked: boolean) => {
    // Add all current viewable content items to selectedContent
    if (checked && content?.items) {
      const allSelectedContent = content.items.map(
        (n: ContentItem) => ({ id: n.id, contentType: n.contentType }) as BulkContentItem,
      )
      setSelectedContent([...(selectedContent || []), ...allSelectedContent])
      return
    }
    // Remove all current viewable content items from selectedContent
    const filterViewableSelectedItems = (selectedContent || []).filter(
      (n) => !content?.items?.find((c) => c.id === n.id),
    )
    setSelectedContent(filterViewableSelectedItems)
  }
  const onSelectContent = (checked: boolean, item: BulkContentItem) => {
    if (checked) {
      setSelectedContent([...(selectedContent || []), item])
      return
    }
    const filteredItems = (selectedContent || []).filter((n) => n.id !== item.id)
    setSelectedContent(filteredItems.length ? filteredItems : undefined)
  }
  const onBulkUpdateContentVisibility = useCallback(
    async (isHidden: boolean) => {
      // Reset Content Success State
      setSaveContentSucceeded(false)
      if (selectedContent) {
        // Filter out any selected content that is not currently viewable in the table
        const filterViewableSelectedItems = selectedContent.filter((n) => content?.items?.find((c) => c.id === n.id))
        await updateContentVisibility({ scid, isHidden, items: filterViewableSelectedItems })
          .then(() => {
            setSaveContentSucceeded(true)
          })
          .catch(() => {
            updateContentVisibilityError()
          })
      }
    },
    [content?.items, scid, selectedContent, updateContentVisibilityError],
  )
  const onBulkDeleteContent = useCallback(async () => {
    // Reset Content Success State
    setSaveContentSucceeded(false)
    if (selectedContent) {
      // Filter out any selected content that is not currently viewable in the table
      const filterViewableSelectedItems = selectedContent.filter((n) => content?.items?.find((c) => c.id === n.id))
      await deleteContent({ scid, items: filterViewableSelectedItems })
        .then(() => {
          // Remove deleted items from selectedContent
          const filterDeletedItems = (selectedContent || []).filter((n) => !content?.items?.find((c) => c.id === n.id))
          setSelectedContent(filterDeletedItems)
          // If we're removing all items in a page, go back a page || stay on first page
          if (filterViewableSelectedItems.length === pageSize && from !== 0) {
            setFrom(from - 1)
          } else {
            // If we're updating the page, we don't need to recall content on success
            setSaveContentSucceeded(true)
          }
        })
        .catch(() => {
          bulkDeleteContentError()
        })
    }
  }, [content?.items, bulkDeleteContentError, from, pageSize, scid, selectedContent])

  /** Highlights Actions */
  const fetchHighlights = useCallback(async () => {
    await getHighlights({ scid })
      .then((response) => {
        setFetchHighlightsLoading(false)
        setHighlights(response.BulletPoints)
        setOverview(response) // TEMPORARY
      })
      .catch(() => {
        setFetchHighlightsLoading(false)
        fetchHighlightsError()
      })
  }, [fetchHighlightsError, scid])

  const saveHighlights = useCallback(
    async (highlights: string[]) => {
      // Reset Highlight State
      setSaveHighlightsSucceeded(false)
      setSaveHighlightsError(false)
      const newOverview = { ...overview, BulletPoints: highlights }
      await updateHighlights({ scid, oldData: overview, newData: newOverview })
        .then(() => {
          setSaveHighlightsSucceeded(true)
        })
        .catch(() => {
          setSaveHighlightsError(true)
        })
      setOverview(newOverview)
    },
    [overview, scid],
  )

  /** Content Actions */

  const fetchContent = useCallback(async () => {
    const defaultParams = {
      from: from * pageSize,
      pageSize: pageSize,
      sortBy: orderBy,
      direction: order,
      isHidden: filterByStatus,
      contentType: filterByType,
      tags: filterByTopics,
      untaggedOnly: untaggedOnly,
    }
    await getContent({ scid, params: defaultParams })
      .then((response) => {
        setFetchContentLoading(false)
        setContent(response)
      })
      .catch(() => {
        setFetchContentLoading(false)
        fetchContentError()
      })
  }, [
    fetchContentError,
    filterByStatus,
    filterByTopics,
    filterByType,
    from,
    order,
    orderBy,
    pageSize,
    scid,
    untaggedOnly,
  ])

  const fetchReorderContent = useCallback(async () => {
    const defaultParams = {
      from: 0,
      pageSize: 0,
    }
    await getContent({ scid, params: defaultParams })
      .then((response) => {
        setFetchReorderContentLoading(false)
        setReorderContent(response)
      })
      .catch(() => {
        setFetchReorderContentLoading(false)
        fetchContentError()
      })
  }, [fetchContentError, scid])

  const handleFocusedItemType = useCallback(
    async (type?: ContentType) => {
      // Reset Content State
      setSaveContentLoading(false)
      setSaveContentSucceeded(false)
      setSaveContentError(false)
      setModalMode('new')
      setFocusedContentItem({
        ...focusedContentItem,
        isHidden: false,
        IsVirtual: false,
        isRecurring: false,
        ContentType: type,
      })
    },
    [focusedContentItem],
  )

  const handleCopyContent = useCallback(
    async (id: string, type: ContentType) => {
      // Reset Content State
      setSaveContentLoading(false)
      setSaveContentSucceeded(false)
      setSaveContentError(false)
      setModalMode('copy')

      // Fetch the full content details first to get all fields including AltText
      await getContentItem({ scid, id, type })
        .then((response) => {
          // Create a copy of the item with cleared fields for new content
          const copiedItem = {
            ...response,
            Id: undefined, // Clear ID to create new content
            CreatedDate: undefined, // Clear creation date
            UpdatedDate: undefined, // Clear update date
            ContentType: type,
            CollegeName: collegeName,
            Scid: scid,
          }

          setFocusedContentItem(copiedItem)
        })
        .catch(() => {
          fetchContentError()
        })
    },
    [collegeName, scid, fetchContentError],
  )

  const fetchContentItemDetails = useCallback(
    async (id: string, type: string) => {
      // Reset Content State
      setSaveContentLoading(false)
      setSaveContentSucceeded(false)
      setSaveContentError(false)
      setModalMode('edit')
      await getContentItem({ scid, id, type })
        .then((response) => {
          setFocusedContentItem({ ...response, ContentType: type, CollegeName: collegeName, Scid: scid })
        })
        .catch(() => {
          fetchContentError()
        })
    },
    [collegeName, fetchContentError, scid],
  )
  const onUpdateContentVisibility = useCallback(
    async (singleItem: BulkContentItem, isHidden: boolean) => {
      // Reset Content Success State
      setSaveContentSucceeded(false)
      await updateContentVisibility({ scid, isHidden, items: [singleItem] })
        .then(() => {
          setSaveContentSucceeded(true)
        })
        .catch(() => {
          updateContentVisibilityError()
        })
    },
    [scid, updateContentVisibilityError],
  )

  const onDeleteContentItem = useCallback(
    async (item: BulkContentItem) => {
      // Reset Content State
      setSaveContentLoading(true)
      setSaveContentSucceeded(false)
      setSaveContentError(false)
      await deleteContent({ scid, items: [item] })
        .then(() => {
          setFocusedContentItem(undefined)
          setSaveContentSucceeded(true)
          setSaveContentLoading(false)
        })
        .catch(() => {
          setSaveContentLoading(false)
          setSaveContentError(true)
        })
    },
    [scid],
  )

  const onSaveContentItem = useCallback(
    async (type: ContentType, item: ContentItemObject, originalItem: ContentItemObject) => {
      // Reset Content State
      setSaveContentLoading(true)
      setSaveContentSucceeded(false)
      setSaveContentError(false)
      if (item.Id && originalItem) {
        // If we have an ID, we're updating an existing item
        await updateContentItem({
          scid,
          type: originalItem.ContentType as ContentType,
          oldData: originalItem,
          // Keep the original content type for the update
          newData: { ...item, ContentType: originalItem.ContentType },
        })
          .then(() => {
            if (type !== originalItem.ContentType) {
              // Update the content type separately
              updateContentPostType({
                scid,
                id: item.Id,
                oldContentType: originalItem.ContentType as ContentType,
                newContentType: type,
              })
                .then(() => {
                  setFocusedContentItem(undefined)
                  setSaveContentSucceeded(true)
                  setSaveContentLoading(false)
                })
                .catch(() => {
                  setSaveContentLoading(false)
                  setSaveContentError(true)
                })
            } else {
              setFocusedContentItem(undefined)
              setSaveContentSucceeded(true)
              setSaveContentLoading(false)
            }
          })
          .catch(() => {
            setSaveContentLoading(false)
            setSaveContentError(true)
          })
      } else {
        // If we don't have an ID, we're creating a new item
        await createContentItem({ scid, type, item })
          .then(() => {
            setFocusedContentItem(undefined)
            setSaveContentSucceeded(true)
            setSaveContentLoading(false)
          })
          .catch(() => {
            setSaveContentLoading(false)
            setSaveContentError(true)
          })
      }
    },
    [scid],
  )

  return (
    <ContentManagementContext.Provider
      value={{
        scid,
        /** Highlights */
        fetchHighlights,
        saveHighlights,
        highlights,
        fetchHighlightsLoading,
        saveHighlightsSucceeded,
        saveHighlightsError,
        /** Edit/Create Content */
        focusedContentItem,
        setFocusedContentItem,
        handleFocusedItemType,
        fetchContentItemDetails,
        handleCopyContent,
        modalMode,
        onDeleteContentItem,
        onSaveContentItem,
        saveContentLoading,
        saveContentError,
        /** Content */
        fetchContent,
        onUpdateContentVisibility,
        content,
        fetchContentLoading,
        saveContentSucceeded,
        /** Reorder */
        fetchReorderContent,
        reorderContent,
        saveNewOrder,
        saveNewOrderError,
        fetchReorderContentLoading,
        /** Table Filtering */
        filterByStatus,
        filterByType,
        filterByTopics,
        handleTableFiltering,
        untaggedOnly,
        /** Table Sorting */
        orderBy,
        order,
        handleTableSorting,
        /** Content Pagination */
        from,
        setFrom,
        pageSize,
        setPageSize,
        /** Bulk Actions */
        selectedContent,
        onSelectContent,
        onSelectAllContent,
        onBulkUpdateContentVisibility,
        onBulkDeleteContent,
      }}
    >
      {children}
    </ContentManagementContext.Provider>
  )
}

export default ContentManagementProvider
